import React, { useEffect, useRef, useReducer, lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Core Components - loaded immediately
import FileUploadSection from '../../features/data-upload/components/FileUploadSection';
import SubmitButton from '../../shared/components/buttons/SubmitButton';
import RestartButton from '../../shared/components/buttons/RestartButton';
import SaveButton from '../../shared/components/buttons/SaveButton';
import ProgressTracker from '../../shared/components/feedback/ProgressTracker';
import ErrorMessage from '../../shared/components/feedback/ErrorMessage';
import SuccessMessage from '../../shared/components/feedback/SuccessMessage';
import WorkflowStepper from '../../shared/components/layout/WorkflowStepper';
import NetworkStatusIndicator from '../../shared/components/feedback/NetworkStatusIndicator';
import ProcessingParametersSection from '../../features/first-processing/components/ProcessingParametersSection';
import ProcessingMethodSection from '../../features/first-processing/components/ProcessingMethodSection';
import DecimalPrecisionSection from '../../shared/components/forms/selectors/DecimalPrecisionSection';
import { UpgradeModal } from '../../features/subscription/components';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

// Heavy Components - lazy loaded
const DataChart = lazy(() => import('../../shared/components/charts/DataChart'));
const FileNameModal = lazy(() => import('../../shared/components/modals/FileNameModal'));

import { usePublishChatContext } from '@/features/chatbot/usePublishChatContext';
import { computeDataProfile } from '@/features/chatbot/computeDataProfile';
import type { ChatField } from '@/features/chatbot/types';
import { computeRawDistribution, parseUtcMs } from '@/features/first-processing/utils/rawDistribution';
import RawDataDistribution from '@/features/first-processing/components/RawDataDistribution';
import { mapFirstProcessingBackendError } from '@/features/first-processing/utils/firstProcessingBackendErrorMapper';

// Hooks
import { useFirstProcessingFile } from '../../features/first-processing/hooks/useFirstProcessingFile';
import { useProcessingParameters, toAnchorStr } from '../../features/first-processing/hooks/useProcessingParameters';
import { useFirstProcessingUpload } from '../../features/first-processing/hooks/useFirstProcessingUpload';
import { useNetworkStatus } from '../../shared/hooks/useNetworkStatus';
import { useWorkflowSteps } from '../../shared/hooks/useWorkflowSteps';
import { useQuotaCheck } from '../../features/subscription/hooks/useQuotaCheck';
import { useKeepAlive } from '@/core/services/keepalive';

// Types & Reducer
import {
  WORKFLOW_STEPS,
  FirstProcessingParams
} from '@/features/first-processing/types/firstProcessing.types';
import { uiReducer, initialUIState } from '@/features/first-processing/reducers/uiReducer';



// Loading fallback component
const ComponentLoader: React.FC = () => (
  <div className="flex justify-center items-center p-4">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
    <span className="ml-2 text-muted-foreground">Loading...</span>
  </div>
);

const FirstProcessingUIImproved: React.FC = () => {
  const { t } = useTranslation('firstProcessing');
  const { t: tDataProcessing } = useTranslation('dataProcessing');

  // State Management with useReducer
  const [uiState, dispatch] = useReducer(uiReducer, initialUIState);

  // Hooks
  const { isOnline } = useNetworkStatus();
  const {
    fileContent,
    fileMetadata,
    timeMinRaw,
    timeMaxRaw,
    tableData,
    timeMetadata,
    fileInputRef,
    error: fileError,
    handleFileChange,
    resetFile,
    selectedFiles,
    firstTwoLines
  } = useFirstProcessingFile({
    onErrorPayload: (payload) => {
      dispatch({ type: 'SET_ERROR_PAYLOAD', payload });
    },
  });
  
  const {
    parameters,
    validationErrors,
    setTss,
    setOffset,
    setMode,
    setIntrplMax,
    validateParameters,
    resetParameters,
    anchorTime,
    setAnchorTime,
    isConst
  } = useProcessingParameters();
  
  const {
    isUploading,
    uploadProgress,
    uploadError,
    chartData,
    uploadFile,
    resetUpload,
    retryUpload
  } = useFirstProcessingUpload();

  // Quota check hooks
  const { canProcess, refreshUsage } = useQuotaCheck();

  // Refs
  const bottomRef = useRef<HTMLDivElement>(null);

  // Keepalive hook - prevents Cloud Run instance termination when tab is minimized
  useKeepAlive(isUploading || uiState.isProcessing);

  // Derive raw timestamps from tableData (skip header row, take UTC column 0)
  const rawTimestamps = useMemo(() => {
    if (!tableData || tableData.length < 2) return [];
    const separator = tableData[0].length > 0 ? tableData[0][0] : '';
    // tableData is already split into rows/cells; UTC is always column 0
    const timestamps: number[] = [];
    for (let i = 1; i < tableData.length; i++) {
      const cell = tableData[i]?.[0];
      if (!cell) continue;
      // Parse as UTC — `new Date(cell)` would read it as local time and inject a
      // DST jump, producing spurious Δt values in the step distribution.
      const parsed = parseUtcMs(cell);
      if (parsed !== null) timestamps.push(parsed);
    }
    timestamps.sort((a, b) => a - b);
    void separator; // suppress unused warning
    return timestamps;
  }, [tableData]);

  const rawDistribution = useMemo(() => computeRawDistribution(rawTimestamps), [rawTimestamps]);

  // Chat context publishing
  const chatContext = useMemo(() => {
    const fields: ChatField[] = [];
    if (parameters.tss !== '') fields.push({ label: 'Zeitschrittweite [min]', value: String(parameters.tss) });
    if (parameters.offset !== '') fields.push({ label: 'Offset [min]', value: String(parameters.offset) });
    if (parameters.mode) fields.push({ label: 'Methode', value: parameters.mode });
    if (parameters.intrplMax !== '') fields.push({ label: 'Max. Interpolation [min]', value: String(parameters.intrplMax) });
    const dataProfile = tableData?.length
      ? computeDataProfile(tableData, (tableData[0] ?? []).map((_, i) => 'col' + i), 0)
      : undefined;
    return { fields, dataProfile };
  }, [parameters, tableData]);

  usePublishChatContext(chatContext);

  // Workflow step management using shared hook
  const { isMountedRef, markStepCompleted, moveToStep } = useWorkflowSteps(dispatch);

  // Combined error handling with queue
  useEffect(() => {
    if (fileError) {
      dispatch({ type: 'ADD_ERROR', payload: fileError });
    }
    if (uploadError) {
      dispatch({ type: 'ADD_ERROR', payload: uploadError });
    }
  }, [fileError, uploadError]);

  // Reset parameters when file is removed (trash icon clicked).
  // Note: this fires anytime selectedFiles becomes empty — including
  // immediately after handleReset() does its own reset. We deliberately
  // DO NOT dispatch RESET_UI here because that would wipe a successMessage
  // that handleReset just set (the Neustart toast). The dispatch responsible
  // for navigating back to step 1 + showing the reset toast lives in
  // handleReset itself; this effect only takes care of in-hook side state.
  useEffect(() => {
    if (selectedFiles.length === 0 && !fileContent) {
      resetParameters();
      resetUpload();
      isMountedRef.current = false;
      setTimeout(() => {
        isMountedRef.current = true;
      }, 100);
    }
  }, [selectedFiles, fileContent, resetParameters, resetUpload, isMountedRef]);

  // Auto-populate anchorTime from first raw timestamp when tss is non-integer divisor of 60
  useEffect(() => {
    if (!isConst && timeMinRaw && !anchorTime) {
      setAnchorTime(toAnchorStr(timeMinRaw));
    }
  }, [isConst, timeMinRaw, anchorTime, setAnchorTime]);

  // Auto-scroll when file is selected (after "Daten auswählen")
  useEffect(() => {
    if (fileContent && tableData.length > 0) {
      // Small delay to ensure DOM elements are rendered
      const scrollTimer = setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [fileContent, tableData]);
  
  // Auto-scroll to chart when results are ready with animation
  useEffect(() => {
    if (chartData.length > 0) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 300);
    }
  }, [chartData]);
  
  // Disable page scrolling when upload is in progress
  useEffect(() => {
    if (isUploading && uploadProgress) {
      const previousBodyOverflow = document.body.style.overflow;
      const previousHtmlOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousBodyOverflow;
        document.documentElement.style.overflow = previousHtmlOverflow;
      };
    }
  }, [isUploading, uploadProgress]);
  
// Handle file upload completion
  useEffect(() => {
    // Only run after component has mounted to prevent auto-progression on refresh
    if (!isMountedRef.current) return;

    if (fileContent && !fileError) {
      markStepCompleted(1);
      if (uiState.currentStep === 1) {
        dispatch({ type: 'SET_SUCCESS', payload: t('uploadSuccess') });
        moveToStep(2);
      }
    }
  }, [fileContent, fileError, markStepCompleted, moveToStep, uiState.currentStep, dispatch, t]);
  
  // Handle parameter completion and auto-advance
  useEffect(() => {
    // Only run after component has mounted
    if (!isMountedRef.current) return;
    
    const tssIsValid = parameters.tss !== '' && !validationErrors.tss;
    const offsetIsValid = parameters.offset !== '' && !validationErrors.offset;
    
    if (tssIsValid && offsetIsValid && fileContent) {
      markStepCompleted(2);
      if (uiState.currentStep === 2) {
        moveToStep(3);
      }
    }
  }, [parameters.tss, parameters.offset, validationErrors.tss, validationErrors.offset, markStepCompleted, uiState.currentStep, moveToStep, fileContent]);
  
  // Handle method selection
  useEffect(() => {
    // Only run after component has mounted
    if (!isMountedRef.current) return;
    
    if (parameters.mode && parameters.mode.length > 0 && uiState.currentStep >= 3 && fileContent) {
      markStepCompleted(3);
    }
  }, [parameters.mode, markStepCompleted, uiState.currentStep, fileContent]);
  
  // Process data with improved error handling
  async function handleProcessData() {
    // Clear previous messages
    dispatch({ type: 'CLEAR_ERRORS' });
    dispatch({ type: 'SET_SUCCESS', payload: null });

    // Validate all inputs
    if (!fileContent) {
      dispatch({ type: 'ADD_ERROR', payload: tDataProcessing('errors.noFile') });
      moveToStep(1);
      return;
    }

    if (!validateParameters()) {
      const firstError = Object.values(validationErrors)[0];
      dispatch({ type: 'ADD_ERROR', payload: firstError || t('allFieldsRequired') });
      // Move to the step with the first error
      if (!parameters.tss || !parameters.offset) {
        moveToStep(2);
      } else if (parameters.mode === 'intrpl' && !parameters.intrplMax) {
        moveToStep(3);
      }
      return;
    }

    if (!isOnline) {
      dispatch({ type: 'ADD_ERROR', payload: t('noInternet') });
      return;
    }

    // Check processing quota
    const processingCheck = await canProcess();
    if (!processingCheck.allowed) {
      dispatch({
        type: 'SET_UPGRADE_MODAL',
        payload: { open: true, message: t('errors.processingLimitReached', 'Processing limit reached'), resourceType: 'processing' },
      });
      return;
    }
    
    // Move to processing step
    moveToStep(4);
    dispatch({ type: 'SET_PROCESSING', payload: true });
    
    const processingParams: FirstProcessingParams = {
      tss: parseInt(parameters.tss),
      offset: parseInt(parameters.offset),
      mode: parameters.mode,
      intrplMax: parameters.mode === 'intrpl' ? parseInt(parameters.intrplMax || '60') : 60,
      decimalPrecision: uiState.decimalPrecision,
      ...(!isConst && anchorTime ? { anchorTime } : {})
    };
    
    try {
      const success = await uploadFile(fileContent, processingParams);

      if (success) {
        // Refresh usage data for next operation
        if (refreshUsage) {
          try {
            await refreshUsage();
          } catch (refreshError) {
            console.error('Failed to refresh usage data:', refreshError);
          }
        }

        markStepCompleted(4);
        moveToStep(5);
        dispatch({ type: 'SET_SUCCESS', payload: t('successProcessed') });
      }
    } catch {
      dispatch({ type: 'ADD_ERROR', payload: t('errorProcessing') });
    } finally {
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  }
  
  // Generate info file content
  const generateInfoFileContent = (): string => {
    const now = new Date();
    const timestamp = now.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    let content = `${t('infoFile.header')}\n`;
    content += `${'='.repeat(60)}\n\n`;

    // Generation timestamp
    content += `${t('infoFile.generatedAt')}: ${timestamp}\n\n`;

    // Uploaded file
    content += `${t('infoFile.uploadedFile')}:\n`;
    content += `${'-'.repeat(40)}\n`;
    content += `${fileMetadata?.name || 'N/A'}\n\n`;

    // Original file parameters (calculated from uploaded file)
    content += `${t('infoFile.originalFileParams')}:\n`;
    content += `${'-'.repeat(40)}\n`;
    content += `${t('infoFile.originalTimestep')}: ${timeMetadata.timestepMinutes ?? 'N/A'} ${t('infoFile.minutes')}\n`;
    content += `${t('infoFile.originalOffset')}: ${timeMetadata.offsetMinutes ?? 'N/A'} ${t('infoFile.minutes')}\n\n`;

    // Processing parameters (user input)
    content += `${t('infoFile.parameters')}:\n`;
    content += `${'-'.repeat(40)}\n`;
    content += `${t('infoFile.timestepSize')}: ${parameters.tss}\n`;
    content += `${t('infoFile.offset')}: ${parameters.offset}\n`;

    // Processing mode with translation
    const modeKey = `infoFile.mode.${parameters.mode}`;
    const modeTranslated = t(modeKey);
    content += `${t('infoFile.processingMode')}: ${modeTranslated}\n`;

    // Interpolation max (only if mode is 'intrpl')
    if (parameters.mode === 'intrpl' && parameters.intrplMax) {
      content += `${t('infoFile.interpolationMax')}: ${parameters.intrplMax}\n`;
    }

    content += `\n${'='.repeat(60)}\n`;

    return content;
  };

  // Download info file
  const downloadInfoFile = (baseFileName: string) => {
    try {
      const infoContent = generateInfoFileContent();
      const blob = new Blob([infoContent], { type: 'text/plain;charset=utf-8' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${baseFileName}_info.txt`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading info file:', error);
      // Don't show error to user as this is a supplementary file
    }
  };

  // Save data with direct browser download (no backend calls needed)
  const handleSaveData = async () => {
    if (chartData.length === 0) {
      dispatch({ type: 'ADD_ERROR', payload: t('noDataToSave') });
      return;
    }

    dispatch({ type: 'SET_SAVING', payload: true });

    try {
      // Find the value column name
      const firstDataPoint = chartData[0];
      const valueColumnName = Object.keys(firstDataPoint).find(
        (key) => key !== 'UTC'
      );

      if (!valueColumnName) {
        throw new Error(t('noValueColumn'));
      }

      // Convert chartData to CSV string directly
      const csvLines = [
        `UTC;${valueColumnName}`,  // Header
        ...chartData.map((item: Record<string, unknown>) => {
          const value = item[valueColumnName];
          // Handle NaN, null, undefined values
          let formattedValue: string;
          if (value === undefined || value === null || value === '') {
            formattedValue = 'nan';
          } else {
            const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
            formattedValue = isNaN(numValue) ? 'nan' : numValue.toString();
          }
          return `${item.UTC};${formattedValue}`;
        })
      ];
      const csvString = csvLines.join('\n');

      // Create blob and trigger direct download (no backend needed!)
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${uiState.saveDataName.trim() || 'processed_data'}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup blob URL
      window.URL.revokeObjectURL(blobUrl);

      // Also download info file with same base name
      downloadInfoFile(uiState.saveDataName.trim() || 'processed_data');

      dispatch({ type: 'SET_SUCCESS', payload: t('fileDownloading') });
      dispatch({ type: 'TOGGLE_MODAL', payload: false });
      markStepCompleted(5);
    } catch (error) {
      dispatch({
        type: 'ADD_ERROR',
        payload: error instanceof Error
          ? error.message
          : t('errorSavingFile')
      });
    } finally {
      dispatch({ type: 'SET_SAVING', payload: false });
    }
  };
  
  // Reset all
  function handleReset() {
    // Reset all states
    resetFile();
    resetParameters();
    resetUpload();
    dispatch({ type: 'SET_DECIMAL_PRECISION', payload: 'full' });
    
    // Atomic reset: returns to initialUIState (currentStep=1, errorMessages=[],
    // saveDataName='', etc.) AND sets successMessage in a single reducer step
    // so the SuccessMessage component gated by {uiState.successMessage && ...}
    // mounts immediately without a null-intermediate flicker.
    dispatch({
      type: 'RESET_UI_WITH_SUCCESS',
      payload: tDataProcessing('messages.resetSuccess'),
    });

    // Reset mounted flag to prevent auto-progression
    isMountedRef.current = false;
    setTimeout(() => {
      isMountedRef.current = true;
    }, 100);
  }
  
  
  return (
    <>
      {/* Message Container */}
      <div className="messages-container">
        <div className="messages-wrapper">
          {uiState.errorMessages.length > 0 && (() => {
            const mapped = mapFirstProcessingBackendError({
              code: uiState.errorCode ?? undefined,
              message: uiState.errorMessages[0],
              suggestion: uiState.errorSuggestion ?? undefined,
            });
            const displayMessage = mapped.translationKey
              ? t(mapped.translationKey, { defaultValue: mapped.fallbackMessage })
              : mapped.fallbackMessage;
            return (
              <ErrorMessage
                message={displayMessage}
                onClose={() => dispatch({ type: 'CLEAR_ERRORS' })}
                duration={5000}
              />
            );
          })()}
          {uiState.successMessage && (
            <SuccessMessage
              message={uiState.successMessage}
              onClose={() => dispatch({ type: 'SET_SUCCESS', payload: null })}
              duration={3000}
            />
          )}
        </div>
      </div>
      
      <NetworkStatusIndicator />
      
      <div className="main-container relative">
        {/* Workflow Stepper */}
        <WorkflowStepper
          steps={WORKFLOW_STEPS}
          currentStep={uiState.currentStep}
          completedSteps={uiState.completedSteps}
          namespace="firstProcessing"
        />
        
        <div className="card max-w-7xl mx-auto bg-card p-8 shadow-lg rounded-lg">
          {/* Progress Overlay */}
          {isUploading && uploadProgress && (
            <div className="fixed inset-0 bg-background/95 z-[9999] flex items-center justify-center overflow-hidden">
              <div className="w-full max-w-lg mx-4 sm:mx-auto p-4 sm:p-6">
                <ProgressTracker
                  progress={uploadProgress}
                  className="my-4 sm:my-8"
                />
                {uploadError && (
                  <div className="mt-4 text-center">
                    <p className="text-red-600 mb-3 text-sm sm:text-base">{uploadError}</p>
                    <button
                      onClick={retryUpload}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors text-sm sm:text-base"
                    >
                      {t('retry', { defaultValue: 'Retry' })}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Step 1: File Upload - Hide after processing starts */}
          {uiState.showFileUpload && (
            <div className="mb-8">
              <FileUploadSection
                onFileChange={handleFileChange}
                fileInputRef={fileInputRef}
                firstTwoLines={firstTwoLines || {}}
                selectedFiles={selectedFiles || []}
                multiple={false}
                onRemoveFile={() => resetFile()}
                dropLabelKeys={{ drop: 'upload.dropFile', dropOr: 'upload.dropFileOr', namespace: 'firstProcessing' }}
                hideDropZoneWhenFileSelected={true}
                showFilePreview={false}
                accept=".csv"
              />
            </div>
          )}
          
          {/* Distribution Charts - Replace raw data table preview */}
          {tableData.length > 0 && uiState.showFileUpload && (
            <div className="mb-8">
              <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4 flex items-center">
                {t('rawDistribution.sectionTitle', { defaultValue: 'Zeitraster' })}
                <InfoTooltip text={t('tooltips.zeitrasterSection', { ns: 'dataUpload' })} width="w-96" />
              </h3>
              <RawDataDistribution distribution={rawDistribution} />
            </div>
          )}
          
          {/* Step 2: Parameters */}
          {fileContent && uiState.currentStep >= 2 && (
            <div className="mb-8">
              <ProcessingParametersSection
                timeMinRaw={timeMinRaw}
                timeMaxRaw={timeMaxRaw}
                tss={parameters.tss}
                offset={parameters.offset}
                onTssChange={setTss}
                onOffsetChange={setOffset}
                validationErrors={validationErrors}
                isConst={isConst}
                anchorTime={anchorTime}
                onAnchorTimeChange={setAnchorTime}
              />
            </div>
          )}
          
          {/* Step 3: Method Selection */}
          {fileContent && parameters.tss !== '' && uiState.currentStep >= 3 && (
            <div className="mb-8">
              <ProcessingMethodSection
                mode={parameters.mode}
                intrplMax={parameters.intrplMax}
                onModeChange={setMode}
                onIntrplMaxChange={setIntrplMax}
                validationErrors={validationErrors}
              />
            </div>
          )}

          {/* Step 4: Decimal Precision (Optional) */}
          {fileContent && uiState.currentStep >= 2 && (
            <DecimalPrecisionSection
              value={uiState.decimalPrecision}
              onChange={(val) => dispatch({ type: 'SET_DECIMAL_PRECISION', payload: val })}
              disabled={isUploading || uiState.isProcessing}
            />
          )}

          {/* Action Buttons with Enhanced States */}
          {fileContent && (
            <div className="mt-8 flex justify-start items-center gap-4 flex-wrap">
              <SubmitButton
                onClick={handleProcessData}
                disabled={
                  isUploading ||
                  uiState.isProcessing ||
                  !parameters.tss ||
                  parameters.offset === '' ||
                  (parameters.mode === 'intrpl' && !parameters.intrplMax) ||
                  Object.values(validationErrors).some(Boolean)
                }
                aria-label="Process data"
              >
                {isUploading || uiState.isProcessing
                  ? tDataProcessing('processing')
                  : tDataProcessing('submitButton')
                }
              </SubmitButton>
              
              <RestartButton
                onRestart={handleReset}
                iconSize={28}
                disabled={isUploading || uiState.isProcessing}
                aria-label="Reset all data"
              />
              
              {chartData.length > 0 && (
                <SaveButton
                  onSave={() => dispatch({ type: 'TOGGLE_MODAL', payload: true })}
                  iconSize={28}
                  disabled={isUploading || uiState.isSaving}
                  aria-label="Save processed data"
                />
              )}
              
              <div ref={bottomRef} />
            </div>
          )}
          
          {/* Step 5: Results - Chart Display with Animation */}
          {chartData.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-foreground mb-1">
                {t('processedData')}
              </h3>
              <Suspense fallback={<ComponentLoader />}>
                <DataChart data={chartData} showSeriesInfo={false} />
              </Suspense>
            </div>
          )}
        </div>
      </div>
      
      {/* Save Modal with Enhanced UX */}
      <Suspense fallback={<ComponentLoader />}>
        <FileNameModal
          isOpen={uiState.isNameModalOpen}
          originalFileName={fileMetadata?.name || ''}
          saveDataName={uiState.saveDataName}
          setSaveDataName={(name: string) => dispatch({ type: 'SET_SAVE_NAME', payload: name })}
          onClose={() => dispatch({ type: 'TOGGLE_MODAL', payload: false })}
          onSave={handleSaveData}
          setError={(error: string) => dispatch({ type: 'ADD_ERROR', payload: error })}
          isLoading={uiState.isSaving}
        />
      </Suspense>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={uiState.showUpgradeModal}
        onClose={() => dispatch({ type: 'SET_UPGRADE_MODAL', payload: { open: false } })}
        resourceType={uiState.upgradeResourceType}
        message={uiState.upgradeMessage}
      />

    </>
  );
};

export default FirstProcessingUIImproved;